import { HStack, Input, InputGroup, Text, VStack } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import {
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { FundingRequestTypeEnumGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { CheckConstraint } from './CheckConstraint';
import { CurrencyInput } from './CurrencyInput';
import { SharedInputLabel, sharedSectionStyles } from './Shared';
import { colors } from 'packages/common-frontend/colors';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { percentageToPercentageLabel } from '~/packages/common-frontend/utils';
import {
  useFundingRequestTemplateContextV1,
  DrawRequest,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';

type DrawRequestProps = {
  drawRequest: DrawRequest;
  type: FundingRequestTypeEnumGQLType;
};

const LoanRequestAmount = ({
  drawRequest: {
    key,
    requestAmount,
    targetFundingDate,
    constraints: {
      minimum: minimumConstraint,
      maximum: maximumConstraint,
      increment: incrementConstraint,
    },
    loan,
  },
  type,
}: DrawRequestProps) => {
  const { context } = useFundingRequestTemplateContextV1();
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const { isSlsAgentLender } = useFeatureFlags();

  finleyAssert(loan, 'Loan attributes must be provided for draw request');

  const {
    pricing: {
      benchmark: { rate: benchmarkRate, determinationDate, name },
      waApplicableMarginRate,
      allInRate,
    },
    maturityDate,
    externalLoanId,
  } = loan;

  const isFixedRate = name.toLowerCase().includes('fixed');

  const updateDrawRequest = useCallback(
    (
      params:
        | { type: 'amount'; amount?: number }
        | { type: 'targetFundingDate'; targetFundingDate?: Util.FullDateStr }
        | { type: 'maturityDate'; maturityDate?: Util.FullDateStr }
        | { type: 'externalLoanId'; externalLoanId?: string }
        | { type: 'determinationDate'; determinationDate?: Util.FullDateStr },
    ) => {
      const drawRequestParams: {
        key: string;
        amount: number;
        targetFundingDate: Util.FullDateStr;
        maturityDate: Util.FullDateStr;
        externalLoanId?: string;
        determinationDate?: Util.FullDateStr;
      } = {
        key,
        amount: requestAmount,
        targetFundingDate,
        maturityDate,
        externalLoanId,
      };
      switch (params.type) {
        case 'amount': {
          if (typeof params.amount === 'number') {
            drawRequestParams.amount = params.amount;
          }
          break;
        }
        case 'externalLoanId': {
          if (
            typeof params.externalLoanId === 'string' ||
            params.externalLoanId === null
          ) {
            drawRequestParams.externalLoanId = params.externalLoanId;
          }
          break;
        }
        case 'maturityDate': {
          if (typeof params.maturityDate === 'string') {
            drawRequestParams.maturityDate = params.maturityDate;

            // Note that this is updating the "global" maturity date.
            context.setValue({
              type: 'setMaturityDate',
              options: { date: params.maturityDate },
            });
          }
          break;
        }
        case 'targetFundingDate': {
          if (typeof params.targetFundingDate === 'string') {
            drawRequestParams.targetFundingDate = params.targetFundingDate;

            // Note that this is updating the "global" target funding date.
            // Should revisit this and likely clean this up as we remove the
            // reliance on "global"
            context.setValue({
              type: 'setTargetFundingDate',
              options: {
                key,
                option: 'draw',
                targetFundingDate: params.targetFundingDate,
              },
            });
          }
          break;
        }
        case 'determinationDate': {
          if (typeof params.determinationDate === 'string') {
            drawRequestParams.determinationDate = params.determinationDate;
          }
          break;
        }
        default:
          finleyAssertUnreachable(params);
      }
      context.setValue({
        type: 'setDrawRequests',
        options: drawRequestParams,
      });
    },
    [
      key,
      context,
      requestAmount,
      targetFundingDate,
      maturityDate,
      externalLoanId,
    ],
  );

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      id="classed-draw-request-amount-stack"
    >
      <HStack {...sharedSectionStyles} p="none" mb="12px">
        <VStack alignItems="flex-start" w="100%">
          <Typography fontWeight="semibold">External Loan ID</Typography>
          <InputGroup w="100%">
            <Input
              w="100%"
              type="text"
              bgColor="white"
              isDisabled={
                context.allowedActions === 'readOnly' || type === 'upsizing'
              }
              onChange={(evt) => {
                updateDrawRequest({
                  type: 'externalLoanId',
                  externalLoanId: evt.target.value,
                });
              }}
              value={externalLoanId ?? undefined}
            />
          </InputGroup>
          <Typography variant="detail-muted">Optional</Typography>
        </VStack>
      </HStack>
      <Typography fontWeight="semibold">Draw Request Amount</Typography>
      <CurrencyInput
        state={requestAmount}
        updateState={(amount) => updateDrawRequest({ type: 'amount', amount })}
        isDisabled={context.allowedActions === 'readOnly'}
      />
      <HStack alignSelf="flex-end" spacing="24px">
        {/* @jendevelops constraints TODO */}
        <CheckConstraint
          type="minimum"
          value={requestAmount}
          constraintValue={minimumConstraint}
        />
        {incrementConstraint && (
          <CheckConstraint
            type="increment"
            value={requestAmount}
            constraintValue={incrementConstraint}
          />
        )}
        <CheckConstraint
          type="maximum"
          value={requestAmount}
          constraintValue={maximumConstraint}
        />
      </HStack>
      {/* @jendevelops TODO - need to find a better way to hide loan related values if no loan mechanics defined */}
      {isSlsAgentLender && (
        <VStack w="100%" alignItems="flex-start" mt="24px">
          {context.mode !== 'upsize' && (
            <>
              <HStack
                justify="space-between"
                w="100%"
                borderBottom={`1px solid ${colors.gray[200]}`}
              >
                <Text fontWeight="600">All-in-Rate</Text>
                <Text fontWeight="600">
                  {percentageToPercentageLabel(allInRate * 100, 5, true)}
                </Text>
              </HStack>
              <HStack justify="space-between" w="100%">
                <Text color="black.70">
                  {isFixedRate ? 'Fixed Rate' : 'Benchmark Rate'}
                </Text>
                <Text color="black.70">
                  {percentageToPercentageLabel(benchmarkRate * 100, 5, true)}
                </Text>
              </HStack>
              {context.allowedActions === 'mutable' &&
                context.mode === 'create' &&
                !isFixedRate && (
                  <HStack justify="space-between" w="100%">
                    <Text color="black.70">Benchmark Determination Date</Text>
                    <InputGroup w="30%">
                      <SimpleDatePicker
                        dateFormat="MMMM dd, yyyy"
                        selectedDate={commonDate.fullDateStrToDate(
                          determinationDate,
                        )}
                        minDate={commonDate.fullDateStrToDate(
                          commonDate.addDays(targetFundingDate, -45),
                        )}
                        maxDate={commonDate.fullDateStrToDate(
                          targetFundingDate,
                        )}
                        setSelectedDate={(date: Date | null) => {
                          if (!date) return;
                          const newDate = commonDate.dateToFullDateString(date);
                          updateDrawRequest({
                            type: 'determinationDate',
                            determinationDate: newDate,
                          });
                        }}
                        width="100%"
                      />
                    </InputGroup>
                  </HStack>
                )}
              <HStack justify="space-between" w="100%">
                <Text color="black.70">WA Applicable Margin</Text>
                <Text color="black.70">
                  {percentageToPercentageLabel(
                    waApplicableMarginRate * 100,
                    5,
                    true,
                  )}
                </Text>
              </HStack>
            </>
          )}
          <HStack
            {...sharedSectionStyles}
            p="none"
            mt={context.mode === 'upsize' ? 0 : '24px'}
          >
            <VStack alignItems="flex-start" w="100%">
              <Typography fontWeight="semibold">Funding Date</Typography>
              <InputGroup w="100%">
                <SimpleDatePicker
                  dateFormat="MMMM dd, yyyy"
                  disabled={context.allowedActions === 'readOnly'}
                  maxDate={commonDate.fullDateStrToDate(
                    commonDate.addDays(maturityDate, -1),
                  )}
                  selectedDate={
                    targetFundingDate
                      ? commonDate.fullDateStrToDate(targetFundingDate)
                      : undefined
                  }
                  setSelectedDate={(date: Date | null) => {
                    if (!date) return;
                    const newDate = commonDate.dateToFullDateString(date);
                    updateDrawRequest({
                      type: 'targetFundingDate',
                      targetFundingDate: newDate,
                    });
                  }}
                  width="100%"
                />
              </InputGroup>
            </VStack>
          </HStack>
          <HStack {...sharedSectionStyles} p="none" mt="24px">
            <VStack alignItems="flex-start" w="100%">
              <Typography fontWeight="semibold">Maturity Date</Typography>
              <InputGroup w="100%">
                <SimpleDatePicker
                  dateFormat="MMMM dd, yyyy"
                  width="100%"
                  disabled={
                    context.allowedActions === 'readOnly' || type === 'upsizing'
                  }
                  selectedDate={
                    maturityDate
                      ? commonDate.fullDateStrToDate(maturityDate)
                      : undefined
                  } // @jendevelops default to max?
                  setSelectedDate={(date: Date | null) => {
                    if (!date) return;
                    const newDate = commonDate.dateToFullDateString(date);
                    updateDrawRequest({
                      type: 'maturityDate',
                      maturityDate: newDate,
                    });
                  }}
                />
              </InputGroup>
            </VStack>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};

export const LoanRequests = ({
  type,
}: {
  type: FundingRequestTypeEnumGQLType;
}) => {
  const {
    context: {
      data: { drawRequests },
    },
  } = useFundingRequestTemplateContextV1();

  return (
    <>
      {drawRequests.map((drawRequest) => (
        <HStack {...sharedSectionStyles} key={drawRequest.key}>
          <SharedInputLabel label={drawRequest.name} />
          <LoanRequestAmount drawRequest={drawRequest} type={type} />
        </HStack>
      ))}
    </>
  );
};
